// Presentation helpers for the Lessons page (spec 044 D9, D13) — pure, so the
// Learner label, the drawer's history lines and the staff-action gates can be
// tested without Firebase.
import { toDate } from "./ticketPresentation";
import { whenLabel } from "./queuePresentation";

// The word staff read for a stored lesson status (047 D4): the server writes "Finished",
// the dashboard says "Completed". Every other status reads as stored.
export const statusWord = (status) => (status === "Finished" ? "Completed" : status);

// functions/createTicket.js's TICKET_CATEGORIES, with the labels the Open-ticket
// dialog shows. Same order, so the two lists can be compared by eye.
export const TICKET_CATEGORIES = [
  { value: "payments", label: "Payments" },
  { value: "account", label: "Account" },
  { value: "app_problem", label: "App problem" },
  { value: "other", label: "Other" },
];

// staffCancelBooking.js MAX_REASON and createTicket.js MAX_TEXT.
export const REASON_MAX = 1000;
export const TICKET_TEXT_MAX = 2000;

// users/{studentId}/children/{childId} — the doc the Learner line reads (D13).
export const childPath = (booking) =>
  booking?.learner?.kind === "child" && booking.learner.childId && booking.studentId
    ? { parentId: booking.studentId, childId: booking.learner.childId }
    : null;

export const childKey = (booking) => {
  const p = childPath(booking);
  return p ? `${p.parentId}/${p.childId}` : null;
};

// D13: for a child, the child's name "for ⟨parent⟩"; otherwise the account holder.
export const learnerLabel = (booking, { childName, parentName } = {}) => {
  const parent = parentName || booking?.studentId || "-";
  if (!childPath(booking)) return parent;
  return `${childName || "Child"} for ${parent}`;
};

// Which side of the lesson a uid is — anyone who is neither party acted as staff.
export const partyOf = (booking, uid) => {
  if (!uid) return null;
  if (uid === booking?.studentId) return "learner";
  if (uid === booking?.teacherId) return "tutor";
  return "staff";
};

const flagsKnown = (b) =>
  [b.countForTeacher, b.donation, b.studentRefund].some((v) => typeof v === "boolean");

// What a cancellation did with the money, from the flags the server writes
// (cancelBooking.js, staffCancelBooking.js). A row from before those flags
// existed says so rather than guessing from timing.
export const cancelOutcome = (b) => {
  if (!b) return "-";
  if (!flagsKnown(b)) return "Not recorded";
  if (b.countForTeacher === true) return "Tutor paid";
  if (b.donation === true) return "Donated";
  if (b.isFreeSession) {
    // The learner's door always hands the free session back; only staff can keep it.
    return b.studentRefund === true || partyOf(b, b.cancelledBy) !== "staff"
      ? "Free session returned"
      : "Free session not returned";
  }
  return b.studentRefund === true ? "Refunded" : "Not refunded";
};

// The drawer's status history (spec: cancelledBy + reason, reportedBy + reason, the
// money flags, the ticket link). Each line: { key, title, detail, chip?, to? }.
export const historyLines = (b, { nameOf = () => undefined, timeZone } = {}) => {
  if (!b) return [];
  const when = (v) => (toDate(v) ? whenLabel(v, { timeZone }) : null);
  const by = (uid) => `${nameOf(uid) || uid} (${partyOf(b, uid)})`;
  const joined = (...parts) => parts.filter(Boolean).join(" · ") || null;
  const lines = [];
  if (b.status === "Cancelled" || b.cancelledBy) {
    lines.push({
      key: "cancelled",
      title: b.cancelledBy ? `Cancelled by ${by(b.cancelledBy)}` : "Cancelled",
      detail: joined(when(b.cancelledAt), b.cancelReason),
      chip: cancelOutcome(b),
    });
  }
  if (b.status === "Finished") {
    lines.push({
      key: "finished",
      title: b.finishedBy ? `Completed by ${by(b.finishedBy)}` : "Completed",
      detail: when(b.finishedAt),
      chip:
        b.countForTeacher === true
          ? "Tutor paid"
          : b.countForTeacher === false
            ? b.isFreeSession
              ? "Free session"
              : "Tutor not paid"
            : null,
    });
  }
  if (b.reportedBy || b.reportReason) {
    lines.push({
      key: "reported",
      title: b.reportedBy ? `Reported by ${by(b.reportedBy)}` : "Reported",
      detail: joined(when(b.reportedAt), b.reportReason),
    });
  }
  if (b.ticketId) {
    lines.push({ key: "ticket", title: "Open the ticket", to: `/inbox/${b.ticketId}` });
  }
  return lines;
};

// When a lesson ended: endTimeUTC, else start + duration (lessonLifecycle's fallback).
export const endOf = (b) => {
  const end = toDate(b?.endTimeUTC);
  if (end) return end;
  const start = toDate(b?.startTimeUTC);
  return start && b?.duration ? new Date(start.getTime() + Number(b.duration) * 60000) : null;
};

// D9: which staff doors a lesson has. Cancel while it is still Pending; Mark
// finished once a Pending lesson has ended — before that, the tutor's own Finish
// button is the door.
export const staffActions = (b, now = new Date()) => {
  const pending = b?.status === "Pending";
  const end = endOf(b);
  return { cancel: pending, finish: pending && !!end && end.getTime() <= now.getTime() };
};
